/**
 * JWT Token Helper for Advertiser Token Management
 *
 * This utility handles JWT token generation and validation for advertiser data.
 * Tokens are valid for 24 hours and stored in localStorage.
 */

const SECRET_KEY = process.env.REACT_APP_JWT_SECRET || 'madkpi-secret-key-for-bot';
const TOKEN_EXPIRY_HOURS = 24;

/**
 * Base64 URL-safe encoding
 */
const base64UrlEncode = (str) => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Base64 URL-safe decoding
 */
const base64UrlDecode = (str) => {
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) {
    str += '=';
  }
  return atob(str);
};

/**
 * Simple HMAC-like signature (for frontend - backend should use proper JWT library)
 */
const createSignature = (data) => {
  // Simple hash function for demonstration
  // Backend should use proper HMAC-SHA256
  const combined = data + SECRET_KEY;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

/**
 * Generate JWT token with advertiser IDs
 * @param {Array} advertiserIds - Array of advertiser ID strings
 * @returns {string} JWT token
 */
export const generateAdvertiserToken = (advertiserIds) => {
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };

  const now = Math.floor(Date.now() / 1000);
  const expiresAt = now + (TOKEN_EXPIRY_HOURS * 60 * 60);

  const payload = {
    advertiserIds: advertiserIds,
    iat: now, // issued at
    exp: expiresAt, // expires at
    iss: 'madkpi-frontend' // issuer
  };

  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const signature = createSignature(`${encodedHeader}.${encodedPayload}`);
  const encodedSignature = base64UrlEncode(signature);

  return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
};

/**
 * Decode JWT token
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
};

/**
 * Get token from localStorage
 * @returns {string|null} Token or null if not found/expired
 */
export const getStoredToken = () => {
  const token = localStorage.getItem('advertiser_token');
  if (!token || isTokenExpired(token)) {
    removeStoredToken();
    return null;
  }
  return token;
};

/**
 * Store token in localStorage
 * @param {string} token - JWT token to store
 */
export const storeToken = (token) => {
  localStorage.setItem('advertiser_token', token);

  // Store timestamp for reference
  const payload = decodeToken(token);
  if (payload && payload.exp) {
    localStorage.setItem('advertiser_token_expiry', payload.exp.toString());
  }
};

/**
 * Remove token from localStorage
 */
export const removeStoredToken = () => {
  localStorage.removeItem('advertiser_token');
  localStorage.removeItem('advertiser_token_expiry');
};

/**
 * Get advertiser IDs from stored token
 * @returns {Array|null} Array of advertiser IDs or null
 */
export const getAdvertiserIdsFromToken = () => {
  const token = getStoredToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.advertiserIds || null;
};
